import React from 'react';
import { Calendar, User, Flag } from 'lucide-react';
import StatusBadge, { PriorityBadge } from '../shared/StatusBadge';

/**
 * Task Card Component
 * Displays a single task in the task list
 *
 * @param {Object} task - Task object from Perfex API
 * @param {boolean} isSelected - Whether this task is currently selected
 * @param {function} onClick - Callback when task is clicked
 */
const TaskCard = ({ task, isSelected, onClick }) => {
    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'No due date';

        const date = new Date(dateString);
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        // Check if today
        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        }

        // Check if tomorrow
        if (date.toDateString() === tomorrow.toDateString()) {
            return 'Tomorrow';
        }

        // Check if overdue
        if (date < today) {
            return <span className="text-red-600 font-semibold">Overdue</span>;
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
    };

    // Get status name (Perfex tasks have status_name or we map from status ID)
    const getStatusName = () => {
        if (task.status_name) return task.status_name;

        // Map status IDs to names (Perfex default statuses)
        const statusMap = {
            1: 'Not Started',
            2: 'In Progress',
            3: 'Testing',
            4: 'Awaiting Feedback',
            5: 'Complete'
        };

        return statusMap[task.status] || 'Unknown';
    };

    // Get assigned user name
    const getAssignedName = () => {
        // Check assignees array first (Perfex CRM format)
        if (task.assignees && task.assignees.length > 0) {
            const assignee = task.assignees[0];
            if (assignee.full_name) return assignee.full_name;
            if (assignee.firstname || assignee.lastname) {
                return `${assignee.firstname || ''} ${assignee.lastname || ''}`.trim();
            }
        }

        // Fallback to other fields
        if (task.assigned_name) return task.assigned_name;
        if (task.assigned) return `User #${task.assigned}`;
        return 'Unassigned';
    };

    return (
        <div
            onClick={onClick}
            className={`p-4 border-b border-gray-200 cursor-pointer transition-all hover:bg-gray-50 ${
                isSelected ? 'bg-green-50 border-l-4 border-l-green-600' : 'border-l-4 border-l-transparent'
            }`}
        >
            {/* Task name/subject */}
            <h3 className={`font-semibold text-gray-800 mb-2 ${isSelected ? 'text-green-700' : ''}`}>
                {task.name || task.subject || 'Untitled Task'}
            </h3>

            {/* Status and Priority badges */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <StatusBadge status={getStatusName()} size="sm" />
                <PriorityBadge priority={task.priority || 2} />
            </div>

            {/* Task metadata */}
            <div className="space-y-1 text-sm text-gray-600">
                {/* Due date */}
                <div className="flex items-center gap-2">
                    <Calendar size={14} className="flex-shrink-0" />
                    <span>{formatDate(task.duedate || task.due_date)}</span>
                </div>

                {/* Assigned to */}
                <div className="flex items-center gap-2">
                    <User size={14} className="flex-shrink-0" />
                    <span>{getAssignedName()}</span>
                </div>
            </div>

            {/* Task description preview (if available) */}
            {task.description && (
                <p className="mt-2 text-xs text-gray-500 line-clamp-2">
                    {task.description.replace(/<[^>]*>/g, '').substring(0, 100)}
                    {task.description.length > 100 ? '...' : ''}
                </p>
            )}
        </div>
    );
};

export default TaskCard;
